import {
  useQuery,
  UseQueryOptions,
  UseQueryResult
} from "@tanstack/react-query";

import { IOptionItem } from "@/components/ui/dropdown/types";
import { AdminAreaData } from "@/types/adminArea";
import { CountryItem } from "@/types/country";

import { getAllAdminAreaApi } from "../adminArea/adminAreaApi";
import { getCountries } from "./countryApi";

export type DataResponse = {
  id: string;
  name: string;
  province_id?: string;
};

export const COUNTRY_KEYS = {
  lv1: ["lv1"] as const
} as const;

export const useCountryQuery = (
  country: IOptionItem | null | undefined, // Allow undefined as well
  options?: Omit<
    | Promise<unknown>[]
    | UseQueryOptions<AdminAreaData | null, Error, AdminAreaData | null>,
    "queryKey" | "queryFn"
  >
): UseQueryResult<AdminAreaData | null, Error> => {
  return useQuery({
    queryKey: [COUNTRY_KEYS.lv1, country],
    queryFn: () => {
      if (!country) return Promise.resolve(null); // Or Promise.resolve([]) if DataResponse is an array
      return getAllAdminAreaApi({
        country_id: country.value,
        level: 1,
        page: 1,
        per_page: 1000
      });
    },
    enabled: !!country,
    retry: 1,
    retryDelay: 1000,
    ...options
  });
};

export const useGetCountries = ({
  params,
  options
}: {
  params?: { subscribed: boolean };
  options?: Omit<
    UseQueryOptions<CountryItem[], Error, CountryItem[]>,
    "queryKey" | "queryFn"
  >;
} = {}) => {
  return useQuery({
    queryKey: ["countries", params?.subscribed],
    queryFn: ({ meta }) =>
      meta?.isOnboarding
        ? (meta.onboardingMockData as CountryItem[])
        : getCountries(params),
    // Country data is relatively static, so we can set a longer stale time
    staleTime: 1000 * 60 * 60 * 24, // 24 hours
    // Keep prefetched data alive for the persister; the default 10min gcTime
    // would evict observer-less queries from memory and IndexedDB
    gcTime: 1000 * 60 * 60 * 24, // 24 hours
    ...options,
    // After the spread so caller options cannot drop persistence
    meta: { ...options?.meta, shouldPersist: true }
  });
};
